/**
 * Portuguese catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade European Portuguese at a mathematician's register.
 * **Warrant → «Garantia»** (Toulmin). W/M grades stay Latin (`::numbering-system/latn`).
 * CLDR cardinal categories are one / many / other (`one` covers 0–1; `many` = the large
 * compact-number form), so `frameActive` declares exactly those (plus an explicit `=0`).
 * Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const PT_CATALOG = {
    locale: 'pt',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Investigação',
        [P2.sectionClaims]: 'Afirmações',
        [P2.sectionIntegrity]: 'Integridade',
        [P2.axisWarrantLabel]: 'Garantia',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empírico',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definição} interp {Interpretação} testable {Testável} tested {Testado} refuted {Refutado} program {Programa de investigação} philos {Filosófico} other {Desconhecido}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Nível de força {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Explorável',
        [P2.stateRefuted]: 'Refutado',
        [P2.stateOutside]: 'Fora do âmbito',
        [P2.verdictConflict]: 'Em conflito com {constraints}',
        [P2.verdictOutside]: 'Necessita de {missing}',
        [P2.lossRegisterPlain]: 'Simplificado para linguagem comum — parte da precisão é omitida.',
        [P2.lossRegisterKid]: 'Simplificado para leitores mais jovens — muitos detalhes são omitidos.',
        [P2.lossProfileDetail]: 'O perfil completo Garantia / Empírico / Modal é resumido aqui.',
        [P2.lossConflictDetail]: 'Os detalhes do conflito são abreviados nesta vista.',
        [P2.laneVerified]: 'Verificado',
        [P2.laneUnverified]: 'Não verificado',
        [P2.laneConflict]: 'Conflito comprovado',
        [P2.constraintLogic]: 'Lógica',
        [P2.constraintLightBends]: 'A luz curva-se num campo gravitacional',
        [P2.constraintParallelPostulate]: 'O postulado das paralelas',
        [P2.claimScalarGravity]: 'Teoria escalar da gravidade',
        [P2.claimNonEuclidean]: 'Geometria não euclidiana',
        [P2.claimSubstrateSr]: 'Interpretação por substrato da relatividade restrita',
        [P2.edgeExtObservation]: 'Observação externa',
        [P2.edgeResourceSample]: 'Recurso computacional partilhado',
        [P2.edgeHeuristicSample]: 'Orientação heurística',
        [P2.edgeSharedSample]: 'Fundamento partilhado',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Restrições',
        [P2.sectionEdges]: 'Arestas',
        [P2.sectionFrame]: 'Quadro',
        [P2.sectionExposure]: 'Exposição',
        [P2.statusAdoptedFormal]: 'Adotado (formal)',
        [P2.statusAdoptedInterpretive]: 'Adotado (interpretativo)',
        [P2.statusSuspended]: 'Suspenso',
        [P2.edgeEffectConstraint]: 'Compõe uma restrição (pode refutar)',
        [P2.edgeEffectResource]: 'Recurso — sem efeito nos veredictos',
        [P2.edgeEffectHeuristic]: 'Heurística — sem efeito nos veredictos',
        [P2.edgeEffectShared]: 'Fundamento partilhado — sem efeito nos veredictos',
        [P2.frameActive]: '{count, plural, =0 {Sem restrições ativas} one {# restrição ativa} many {# restrições ativas} other {# restrições ativas}}',
        [P2.exposureNone]: 'Sem dependências interpretativas',
        [P2.exposureDep]: 'Depende de {id}',
        [P2.laneTrackMachine]: 'Máquina',
        [P2.laneTrackExpert]: 'Especialista',
        [P2.laneTrackCommunity]: 'Comunidade',
        [P2.expertAudited]: 'Auditado',
        [P2.expertInProgress]: 'Em curso',
        [P2.expertNotReviewed]: 'Não revisto',
        [P2.communityFlags]: 'Há sinalizações',
        [P2.communityNone]: 'Sem sinalizações',
        [P2.controlChallenge]: 'Contestar',
        [P2.controlActivate]: 'Ativar',
        [P2.controlMakeInterpretive]: 'Tornar interpretativo',
        [P2.controlImportEdge]: 'Importar aresta',
        [P2.controlPromote]: 'Promover',
        [P2.controlUndo]: 'Desfazer',
        [P2.controlRedo]: 'Refazer',
        [P2.controlRegister]: 'Registar',
        [P2.promoteRejected]: 'Promoção rejeitada',
        [P2.promoteAccepted]: 'Promoção aceite',
        [P2.promoteReasonRefutationTerminal]: 'Uma demonstração não pode sanar uma refutação.',
        [P2.promoteReasonKeystone]: 'Uma promoção fundamental requer apoio empírico.',
        [P2.promoteReasonEmpiricalNotFixed]: 'O tipo empírico não está fixado.',
        [P2.promoteReasonModalNotFixed]: 'O grau modal não está fixado.',
        [P2.promoteReasonWarrantNotImproved]: 'O grau de garantia não melhorou.',
        [P2.evidenceComputation]: "{engine} calculou {op} = {result} sob {config}",
        [P2.lossNumericRounding]: "Resultado arredondado para {dp} casas decimais",
    },
};
//# sourceMappingURL=pt.js.map